import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { translations as zhTranslations } from '../translations/zh';
import { translations as enTranslations } from '../translations/en';

const languageMap = {
  en: enTranslations,
  zh: zhTranslations
};

export type LandingLanguage = keyof typeof languageMap;
export type LandingTranslations = typeof enTranslations;

interface LandingTranslationContextValue {
  language: LandingLanguage;
  setLanguage: (language: LandingLanguage) => void;
  translations: LandingTranslations;
}

const LandingTranslationContext = createContext<LandingTranslationContextValue>({
  language: 'en',
  setLanguage: () => undefined,
  translations: languageMap.en
});

const getInitialLanguage = (): LandingLanguage => {
  if (typeof window !== 'undefined') {
    const stored = window.localStorage.getItem('landingLanguage');
    if (stored === 'en' || stored === 'zh') {
      return stored;
    }
  }
  return 'en';
};

export const LandingTranslationProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [language, setLanguage] = useState<LandingLanguage>(getInitialLanguage);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('landingLanguage', language);
    }
  }, [language]);

  const value = useMemo(
    () => ({
      language,
      setLanguage,
      translations: languageMap[language]
    }),
    [language]
  );

  return (
    <LandingTranslationContext.Provider value={value}>
      {children}
    </LandingTranslationContext.Provider>
  );
};

export const useLandingTranslations = () => useContext(LandingTranslationContext);

